import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { take, tap } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';
import { CalculateOfferResponse } from '../../shared/interfaces/calculate-offer-response.interface';
import { CreationLockResponse } from '../../shared/interfaces/creation-lock-response.interface';
import { captureSentryException } from '../../../utils/sentry-http-error.util';
import { assignNewDiscountAmount } from '../helpers/assign-new-discount-amount';
import { PolicyCreatorService } from '../policy-creator.service';
import {
  ApplyAffiliationDiscount,
  ApplyDiscountCode,
  CalculateOffer,
  GetPolicy,
  GetPolicyByNumber,
  LockOffer,
  ResetDiscountCode,
  ResetOfferState,
  SignOffer,
} from './offer.action';
import { defaults, OfferStateModel } from './offer.model';
import { isNumber } from 'lodash';

@State<OfferStateModel>({
  name: 'offerState',
  defaults,
})
@Injectable()
export class OfferState {
  constructor(private policyCreatorService: PolicyCreatorService) {}

  private setSessionItem(key: string, value: string | null | undefined): void {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    try {
      if (value === null || value === undefined || value === '') {
        window.sessionStorage.removeItem(key);
      } else {
        window.sessionStorage.setItem(key, value);
      }
    } catch {
      // Ignore storage failures (e.g. blocked storage)
    }
  }

  @Selector()
  static offer({ offer }: OfferStateModel): CalculateOfferResponse {
    return offer;
  }

  @Selector()
  static offerLocked({ offerLocked }: OfferStateModel): CreationLockResponse {
    return offerLocked;
  }

  @Selector()
  static isLoading({ isLoading }: OfferStateModel): boolean {
    return isLoading;
  }

  @Action(CalculateOffer, { cancelUncompleted: true })
  public calculateOffer(
    { getState, patchState }: StateContext<OfferStateModel>,
    { payload }: CalculateOffer,
  ) {
    patchState({ isLoading: true });
    const offerState = getState().offer;

    return this.policyCreatorService.getOffer(payload).pipe(
      tap({
        next: (offer) => {
          // Recalculate premiumDiscounted if there's an existing discount
          let recalculatedOffer = {
            ...offer,
            discountCode: offerState?.discountCode,
            discountAmount: offerState?.discountAmount,
            discountLink: offerState?.discountLink,
          };

          if (offerState?.discountAmount && offer.premiumSuggested) {
            const discountValue =
              offer.premiumSuggested -
              (offer.premiumSuggested * Number(offerState.discountAmount)) /
                100;
            if (discountValue >= offer.premiumMin) {
              recalculatedOffer.premiumDiscounted = discountValue;
            }
          }

          patchState({
            offer: recalculatedOffer,
            isLoading: false,
          });
        },
        error: (error) => {
          console.error('[Offer State] Offer calculation failed', error);
          captureSentryException(error);
          patchState({ offer: null, isLoading: false });
        },
      }),
    );
  }

  @Action(SignOffer)
  public signOffer(
    ctx: StateContext<OfferStateModel>,
    { id, payload }: SignOffer,
  ) {
    console.log('[Offer State] Signing offer', { policyId: id });
    Sentry.addBreadcrumb({
      category: 'payment',
      message: 'Signing offer in state',
      level: 'info',
      data: { policyId: id },
    });

    const state = ctx.getState();
    const offerLockedState = state.offerLocked;
    return this.policyCreatorService.signOffer(id, payload).pipe(
      tap({
        next: (response) => {
          console.log('[Offer State] Offer signed successfully', response);
          Sentry.addBreadcrumb({
            category: 'payment',
            message: 'Offer signed successfully',
            level: 'info',
            data: { policyId: id },
          });

          ctx.setState({
            ...state,
            ...offerLockedState,
          });
        },
        error: (error) => {
          console.error('[Offer State] Offer signing failed', error);
          captureSentryException(error);
        },
      }),
    );
  }

  @Action(LockOffer)
  public lockOffer(ctx: StateContext<OfferStateModel>, { payload }: LockOffer) {
    console.log('[Offer State] Locking offer with payload', {
      signatureType: payload.signatureTypeCode,
      productCode: payload.productCode,
    });

    Sentry.addBreadcrumb({
      category: 'payment',
      message: 'Locking offer in state',
      level: 'info',
      data: { signatureType: payload.signatureTypeCode },
    });

    return this.policyCreatorService.lockOffer(payload).pipe(
      take(1),
      tap({
        next: (offer) => {
          console.log('[Offer State] Offer locked successfully', {
            id: offer.id,
            number: offer.number,
            signatureState: offer.signatureState,
            paymentUrl: !!offer.paymentInitiationUrl,
          });

          Sentry.addBreadcrumb({
            category: 'payment',
            message: 'Offer locked successfully',
            level: 'info',
            data: {
              policyId: offer.id,
              policyNumber: offer.number,
              signatureState: offer.signatureState,
            },
          });

          ctx.patchState({ offerLocked: offer });
          return offer;
        },
        error: (error) => {
          console.error('[Offer State] Offer locking failed', error);
          captureSentryException(error);
          ctx.patchState({ offerLocked: null });
        },
      }),
    );
  }

  @Action(GetPolicyByNumber)
  public getPolicyByNumber(
    ctx: StateContext<OfferStateModel>,
    { policyNo }: GetPolicyByNumber,
  ) {
    console.log('[Offer State] Getting policy by number', { policyNo });

    return this.policyCreatorService.getPolicyByNumber(policyNo).pipe(
      take(1),
      tap({
        next: (res) => {
          if (res && res.length > 0) {
            console.log('[Offer State] Found policy by number', {
              id: res[0].id,
              signatureState: res[0].signatureState,
              paidOn: res[0].paidOn,
            });

            ctx.patchState({
              offerLocked: {
                ...res[0],
              },
            });
          } else {
            console.log('[Offer State] No policy found by number', {
              policyNo,
            });
          }
        },
        error: (error) => {
          console.error('[Offer State] Error getting policy by number', error);
          captureSentryException(error);
        },
      }),
    );
  }

  @Action(GetPolicy)
  public getPolicy(ctx: StateContext<OfferStateModel>, { id }: GetPolicy) {
    console.log('[Offer State] Getting policy by id', { id });

    return this.policyCreatorService.getPolicy(id).pipe(
      take(1),
      tap({
        next: (policy) => {
          console.log('[Offer State] Policy retrieved successfully', {
            id: policy.id,
            number: policy.number,
            signatureState: policy.signatureState,
            paidOn: policy.paidOn,
          });

          ctx.patchState({
            offerLocked: {
              ...policy,
            },
          });
        },
        error: (error) => {
          console.error('[Offer State] Error getting policy', error);
          captureSentryException(error);
        },
      }),
    );
  }

  @Action(ResetOfferState)
  public resetState({ getState, setState }: StateContext<OfferStateModel>) {
    // if affiliation discount is applied, keep it, but reset date to defaults
    const currentOffer = getState().offer;
    if (currentOffer?.discountLink) {
      setState({
        ...defaults,
        offer: {
          discountLink: currentOffer.discountLink,
          discountAmount: currentOffer.discountAmount,
        } as CalculateOfferResponse,
      });
    } else {
      setState(defaults);
    }
  }

  @Action(ResetDiscountCode)
  public resetDiscountCode({
    getState,
    patchState,
  }: StateContext<OfferStateModel>) {
    const offer = getState().offer;

    if (offer) {
      offer.discountCode = null;
      offer.discountAmount = null;
      offer.premiumDiscounted = null;
    }

    patchState({
      offer,
    });
  }

  @Action(ApplyDiscountCode)
  public applyDiscountCode(
    { getState, patchState }: StateContext<OfferStateModel>,
    { code, amount }: ApplyDiscountCode,
  ) {
    const offer = getState().offer;
    if (offer) {
      offer.discountCode = code;
      if (!offer.discountAmount) {
        offer.discountAmount = amount;
      }

      offer.discountAmount =
        isNumber(Number(amount))
          ? amount
          : offer.discountAmount;

      const { premiumMin, premiumSuggested, discountAmount } = offer;

      if (!amount) {
        offer.discountAmount = null;
        offer.premiumDiscounted = null;
      }

      if (premiumSuggested && !!amount) {
        const discountValue =
          premiumSuggested - (premiumSuggested * Number(discountAmount)) / 100;
        if (discountValue >= premiumMin) {
          offer.premiumDiscounted = discountValue;
        }
      }
    }

    patchState({
      offer,
    });
  }

  @Action(ApplyAffiliationDiscount)
  public applyAffiliationDiscount(
    { getState, patchState }: StateContext<OfferStateModel>,
    { link, amount }: ApplyAffiliationDiscount,
  ) {
    const offerOrigin = getState().offer;
    const offer = {
      ...offerOrigin,
      discountLink: link,
      discountAmount: assignNewDiscountAmount(amount, offerOrigin),
    };

    if (!amount) {
      offer.discountAmount = null;
      offer.premiumDiscounted = null;
    }

    // Persist affiliation details for the current browser session
    this.setSessionItem('affiliationLink', link);
    this.setSessionItem('affiliationDiscount', amount);

    if (offer.premiumSuggested && !!amount) {
      const discountValue =
        offer.premiumSuggested -
        (offer.premiumSuggested * Number(offer.discountAmount)) / 100;
      if (discountValue >= offer.premiumMin) {
        offer.premiumDiscounted = discountValue;
      }
    }

    patchState({
      offer,
    });
  }
}
